import { Outlet } from 'react-router-dom';
import { AppSidebar } from './AppSidebar';
import { AppHeader } from './AppHeader';
import { DeletionGraceBanner } from '@/components/DeletionGraceBanner';
import { AnchorRevealGate } from '@/components/vision/AnchorRevealGate';
import { useNoIndex } from '@/hooks/useNoIndex';
import { ReverbStatusProvider } from '@/contexts/ReverbStatusContext';
import { VisionSelectionProvider } from '@/contexts/VisionSelectionContext';

/**
 * AppLayout — authenticated app shell.
 *
 * Sidebar + main content; DeletionGraceBanner mounts inline above <Outlet />
 * so it persists across every /app/* page (D-12 cancel path #1).
 *
 * noindex: every /app/* route is private and must not appear in search
 * results. Applying useNoIndex at the layout level means future authenticated
 * pages added under /app/* inherit the meta tag automatically. To opt a
 * specific page back into indexing (rare, but possible), render it outside
 * AppLayout.
 */
export function AppLayout() {
  useNoIndex();
  // ReverbStatusProvider owns the single useReverbChannel() lifecycle for the
  // authenticated session — mounted here (inside AppLayout, which is behind
  // ProtectedRoute) so the WS subscription only runs for authed users and
  // tears down on logout/route change. AppSidebar consumes the status to
  // render its connection dot.
  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <ReverbStatusProvider>
        {/* The reveal overlay and the routed page are SIBLINGS, so confirming a
            selection uncovers a page that was never told. The provider has to
            enclose both halves of that sentence: AnchorRevealGate publishes,
            <Outlet />'s page subscribes. */}
        <VisionSelectionProvider>
          {/* Phase 10 (D-11 / VIS-04) — the post-onboarding ceremonial reveal.
              Renders null unless the user has a ready+unselected Anchor set, in
              which case it takes over the viewport as a full-screen overlay.
              Mounted inside ReverbStatusProvider so CandidateReveal's
              useReverbChannel shares the single authed WS lifecycle. */}
          <AnchorRevealGate />
          <AppSidebar />
          <div className="flex-1 flex flex-col min-w-0">
            {/* Renders at every viewport. Owns mobile navigation and the
                connection dot — see AppHeader for why the dot lives there and
                not in the lg-only sidebar. */}
            <AppHeader />
            <DeletionGraceBanner />
            <main className="flex-1 p-6 lg:p-8 overflow-auto">
              <Outlet />
            </main>
          </div>
        </VisionSelectionProvider>
      </ReverbStatusProvider>
    </div>
  );
}
